'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Office {
  city: string;
  country: string;
  address: string;
  phone: string;
  email: string;
  hours: string;
  lat: number;
  lng: number;
}

const OfficeLocationsSection = () => {
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);

  const offices: Office[] = [
    {
      city: 'Singapore',
      country: 'Singapore',
      address: '123 Orchard Road, #05-01, Singapore 238858',
      phone: '+65 6789 1234',
      email: 'singapore@yunova.com',
      hours: 'Mon-Fri: 9:00 AM - 6:00 PM SGT',
      lat: 1.3048,
      lng: 103.8318
    },
    {
      city: 'Dubai',
      country: 'United Arab Emirates',
      address: 'Office 401, Business Bay Tower, Dubai, UAE',
      phone: '+971 4 567 8900',
      email: 'dubai@yunova.com',
      hours: 'Sun-Thu: 9:00 AM - 6:00 PM GST',
      lat: 25.1972,
      lng: 55.2744
    },
    {
      city: 'Mumbai',
      country: 'India',
      address: '456 Linking Road, Bandra West, Mumbai 400050',
      phone: '+91 22 4567 8900',
      email: 'mumbai@yunova.com',
      hours: 'Mon-Sat: 10:00 AM - 7:00 PM IST',
      lat: 19.0596,
      lng: 72.8295
    }
  ];

  return (
    <section className="bg-muted py-16 md:py-24 hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Our Global Offices
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Visit us at any of our strategically located offices across Asia and the Middle East for personalized consultation and support.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {offices.map((office, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-xl p-6 hover:shadow-hover transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedOffice(office)}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPinIcon" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">{office.city}</h3>
                  <p className="text-sm text-muted-foreground">{office.country}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Icon name="BuildingOfficeIcon" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground">{office.address}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="PhoneIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                  <p className="text-sm text-foreground">{office.phone}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="EnvelopeIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                  <p className="text-sm text-foreground">{office.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="ClockIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                  <p className="text-sm text-foreground">{office.hours}</p>
                </div>
              </div>
              <button className="mt-4 w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold">
                View on Map
              </button>
            </div>
          ))}
        </div>

        {selectedOffice && (
          <div 
            className="fixed inset-0 bg-foreground/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedOffice(null)}
          >
            <div 
              className="bg-card rounded-2xl max-w-4xl w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-96">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`${selectedOffice.city} Office Location`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${selectedOffice.lat},${selectedOffice.lng}&z=14&output=embed`}
                  className="border-0"
                />
                <button
                  onClick={() => setSelectedOffice(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-card rounded-full flex items-center justify-center hover:bg-muted transition-colors shadow-elevated"
                >
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">{selectedOffice.city} Office</h3>
                <p className="text-muted-foreground mb-6">{selectedOffice.country}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Icon name="MapPinIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Address</p>
                      <p className="text-sm text-muted-foreground">{selectedOffice.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="PhoneIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Phone</p>
                      <p className="text-sm text-muted-foreground">{selectedOffice.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="EnvelopeIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Email</p>
                      <p className="text-sm text-muted-foreground">{selectedOffice.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="ClockIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Office Hours</p>
                      <p className="text-sm text-muted-foreground">{selectedOffice.hours}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OfficeLocationsSection;